import { Link, useLocation } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFavorites } from '@/hooks/use-favorites'

interface NavItemProps {
  label: string
  href: string
  nested?: boolean
  icon?: LucideIcon
  /** Render a pin/unpin star (revealed on hover/focus). Expanded sidebar only. */
  showPin?: boolean
  /** Called after the link is clicked (e.g. to close a mobile drawer or flyout). */
  onNavigate?: () => void
}

function PinButton({ href, label }: { href: string; label: string }) {
  const { isFavorite, toggleFavorite } = useFavorites()
  const pinned = isFavorite(href)

  return (
    <button
      type="button"
      onClick={() => toggleFavorite(href)}
      aria-pressed={pinned}
      aria-label={pinned ? `Unpin ${label} from favorites` : `Pin ${label} to favorites`}
      className={cn(
        'absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground transition-opacity',
        'hover:text-foreground hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:opacity-100',
        'opacity-40 group-hover/navitem:opacity-100 group-focus-within/navitem:opacity-100',
        pinned && 'opacity-100 text-primary'
      )}
    >
      <Star className={cn('h-3.5 w-3.5', pinned && 'fill-current')} aria-hidden="true" />
    </button>
  )
}

export function NavItem({
  label,
  href,
  nested = false,
  icon: Icon,
  showPin,
  onNavigate,
}: NavItemProps) {
  const location = useLocation()
  const isActive = location.pathname === href

  return (
    <div className="relative group/navitem">
      <Link
        to={href}
        onClick={onNavigate}
        className={cn(
          'flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          nested && 'pl-9',
          showPin && 'pr-8',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )}
        aria-current={isActive ? 'page' : undefined}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
        <span className="truncate">{label}</span>
      </Link>
      {showPin && <PinButton href={href} label={label} />}
    </div>
  )
}
